import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { DurationTimer } from "@/components/DurationTimer";
import { useDate } from "@/utility/useDate";
import { options } from "@/utility/options";

interface IStatusBoxProps {
  status: string;
}

// Ideally this is config. We _could_ support prod -> staging, staging -> prod, prod -> pre-prod etc.
const TARGET_ENV_URL = "https://app-preprod.parkit.cloud"

const StatusBox: FC<IStatusBoxProps> = ({ status }) => {
  const { t } = useTranslation();

  if (!(["COMPLETED", "FAILED"].includes(status))) {
    return null;
  }

  const colorMap = new Map([
    ["COMPLETED", "success.main"],
    ["FAILED", "error.main"],
  ]);
  const txKeyMap = new Map([
    ["COMPLETED", "completed"],
    ["FAILED", "failed"],
  ]);
  const iconMap = new Map([
    ["COMPLETED", "✓"],
    ["FAILED", "✗"],
  ]);

  return (
    <Box
      sx={{
        p: 2,
        mt: 2,
        borderRadius: 1,
        border: "1px solid",
        borderColor: colorMap.get(status),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: colorMap.get(status),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            {iconMap.get(status)}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
              {t(`taskRunner.copyTenantData.${txKeyMap.get(status)}.title`)}
            </Typography>
            <Typography variant="caption">
              {t(`taskRunner.copyTenantData.${txKeyMap.get(status)}.subtitle`)}
              {
                status === 'COMPLETED' ?
                  <a target="_blank" rel="noreferrer" href={TARGET_ENV_URL}>{TARGET_ENV_URL}</a>
                  : null
              }
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

type StepViewModel = {
  i18nKey: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  stepId?: string;
};

const isSettled = (s: string) =>
  ["COMPLETED", "SKIPPED", "FAILED", "CANCELLED"].includes(s);

const isError = (s: string) =>
  ["FAILED", "CANCELLED"].includes(s);

function statusChipProps(status: string) {
  switch (status) {
    case 'RUNNING':
      return {
        i18nKey: "taskRunner.status.running",
        color: "info" as const,
        variant: "filled" as const
      };
    case 'PENDING':
      return {
        i18nKey: "taskRunner.status.queued",
        color: "default" as const,
        variant: "outlined" as const
      };
    case 'COMPLETED':
      return {
        i18nKey: "taskRunner.status.completed",
        color: "success" as const,
        variant: "filled" as const
      };
    case 'SKIPPED':
      return {
        i18nKey: "taskRunner.status.skipped",
        color: "warning" as const,
        variant: "outlined" as const
      };
    case 'FAILED':
      return {
        i18nKey: "taskRunner.status.failed",
        color: "error" as const,
        variant: "filled" as const
      };
    case 'CANCELLED':
      return {
        i18nKey: "taskRunner.status.cancelled",
        color: "error" as const,
        variant: "outlined" as const
      };
    default:
      return {
        i18nKey: String(status),
        color: "info" as const,
        variant: "outlined" as const
      };
  }
}

const statusOrder = (status: string) =>
  status === "RUNNING" ? 0 :
    status === "PENDING" ? 1 :
      status === "COMPLETED" ? 2 :
        status === "SKIPPED" ? 3 :
          status === "CANCELLED" ? 4 :
            status === "FAILED" ? 5 : 99;

interface StepRowProps {
  label: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
}

const StepRow: FC<StepRowProps> = ({ label, status, startedAt, completedAt }) => {
  const { t } = useTranslation();
  const { i18nKey, ...chipProps } = statusChipProps(status);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        py: 1,
        opacity: ['SKIPPED', 'PENDING'].includes(status) ? 0.6 : 1
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          aria-live="polite"
          sx={{
            fontWeight: status === 'RUNNING' ? 'bold' : 'normal',
            color: isError(status) ? "error.main" : "text.primary",
            wordBreak: "break-word",
            ...(status === 'RUNNING' ? {
              '&::after': {
                content: '"..."',
                display: 'inline-block',
                width: '1.25em',
                textAlign: 'left',
                animation: 'ellipsis 1.6s infinite steps(4)',
              },
              '@keyframes ellipsis': {
                '0%': { content: '""' },
                '25%': { content: '"."' },
                '50%': { content: '".."' },
                '75%': { content: '"..."' },
              },
            } : {})
          }}
        >
          {label}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
          <Chip size="small" {...chipProps} label={t(i18nKey)} sx={{ textTransform: 'uppercase', fontWeight: 'bold', fontSize: 'smaller' }} />
          <Typography variant="caption" color="text.secondary">
            <DurationTimer
              startTime={startedAt}
              endTime={completedAt}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface UATSnapshotStatusProps {
  state: Record<string, unknown>;
}

export const UATSnapshotStatus: FC<UATSnapshotStatusProps> = ({ state }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const { toFormattedDateTimeString } = useDate()

  const typedSteps: StepViewModel[] = (state.steps || []) as StepViewModel[];
  const totalSteps = typedSteps.length;
  const hasFailed = state.status === "FAILED"

  const runningCount = useMemo(
    () => typedSteps.filter(s => s.status === "RUNNING").length,
    [typedSteps],
  );
  const queuedCount = useMemo(
    () => typedSteps.filter(s => s.status === "PENDING").length,
    [typedSteps],
  );
  const doneCount = useMemo(
    () => typedSteps.filter(s => isSettled(s.status)).length,
    [typedSteps],
  );
  const sortedSteps = useMemo(
    () => [...typedSteps].sort((a, b) => statusOrder(a.status) - statusOrder(b.status)),
    [typedSteps],
  );

  useEffect(() => {
    if (state.status === "COMPLETED") {
      setProgress(100);
      return;
    }

    const finished = typedSteps.filter(s => isSettled(s.status)).length;
    const nextProgress = totalSteps === 0 ? 100 : Math.round((finished / totalSteps) * 100);
    setProgress(nextProgress);
  }, [state, typedSteps, totalSteps]);

  const summaryText = useMemo(() => {
    const completedAt = typeof state.completedAt === 'string' ? toFormattedDateTimeString(state.completedAt) : null

    if (state.status === "FAILED") {
      return t("taskRunner.copyTenantData.progress.failed");
    }
    if (state.status === "COMPLETED") {
      return t("taskRunner.copyTenantData.progress.completed", { completedAt });
    }

    return t("taskRunner.copyTenantData.progress.summary", {
      runningCount,
      queuedCount,
      doneCount,
      defaultValue: `${runningCount} running • ${queuedCount} queued • ${doneCount} completed`,
    });
  }, [state.status, runningCount, queuedCount, doneCount, totalSteps]);

  if (!totalSteps) {
    return null
  }

  return (
    <Card sx={{ mt: 2 }} variant="outlined" data-testid="uat-snapshot-status-card">
      <CardContent>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" gutterBottom>
            {t("taskRunner.copyTenantData.progress.title")}
          </Typography>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {summaryText}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress}
            data-testid="progress-bar"
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: (theme) => theme.palette.grey[300],
              "& .MuiLinearProgress-bar": {
                backgroundColor: (theme) =>
                  hasFailed ? theme.palette.error.main : theme.palette.success.main,
              },
            }}
          />
        </Box>

        <Box data-testid="progress-steps-list">
          {sortedSteps.map((step, index) => (
            <StepRow
              key={step.stepId ?? `${step.i18nKey}-${index}`}
              label={t(`taskRunner.copyTenantData.steps.${step.i18nKey}`, { env: options.environment })}
              status={step.status}
              startedAt={step.startedAt}
              completedAt={step.completedAt}
            />
          ))}
        </Box>
        {state.status ? <StatusBox status={String(state.status)} /> : null}
      </CardContent>
    </Card>
  );
};
